import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { DefaultApolloClient } from '@vue/apollo-composable'

// Cache persistence helpers
const CACHE_VERSION = '1.0'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const CACHE_KEY = '__apollo_cache__imagic_map'

const persistCache = (cache) => {
  try {
    const cacheData = cache.extract()
    const dataToStore = {
      version: CACHE_VERSION,
      data: cacheData,
      timestamp: Date.now()
    }
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(dataToStore))
  } catch (error) {
    console.warn('[Apollo] Failed to persist cache:', error)
  }
}

const restoreCache = () => {
  try {
    const stored = sessionStorage.getItem(CACHE_KEY)
    if (!stored) return null
    
    const { version, data, timestamp } = JSON.parse(stored)
    
    // Check version compatibility
    if (version !== CACHE_VERSION) {
      console.log('[Apollo] Cache version mismatch, clearing old cache')
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    
    // Check if cache is expired
    const cacheAge = Date.now() - timestamp
    if (cacheAge > CACHE_TTL) {
      console.log('[Apollo] Cache expired, clearing', { 
        age: Math.round(cacheAge / 1000) + 's',
        ttl: Math.round(CACHE_TTL / 1000) + 's'
      })
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    
    console.log('[Apollo] 📦 Restoring cache from sessionStorage', { 
      cacheSize: Object.keys(data).length,
      age: Math.round(cacheAge / 1000) + 's'
    })
    return data
  } catch (error) {
    console.warn('[Apollo] Failed to restore cache:', error)
    return null
  }
}

// Create or get singleton Apollo Client instance
// Store in window to persist across page navigations
export function createApolloClient() {
  // Return existing instance from window if already created
  if (window.__apolloClientInstance) {
    const clientId = window.__apolloClientInstance._clientId || 'unknown'
    const cacheData = window.__apolloClientInstance.cache?.extract?.() || {}
    const cacheSize = Object.keys(cacheData).length
    console.log(`[Apollo] ✓ Reusing existing client (ID: ${clientId})`, { 
      clientId,
      cacheSize,
      page: window.location.pathname
    })
    return window.__apolloClientInstance
  }
  
  // Check if client is being created (prevent race conditions)
  if (window.__apolloClientCreating) {
    const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown'
    console.log('[Apollo] ⏳ Apollo Client creation in progress, waiting...', {
      page: window.location.pathname,
      caller
    })
    // Wait synchronously (not ideal, but handles the race condition)
    let attempts = 0
    const maxAttempts = 100 // Max 100ms wait
    const startTime = Date.now()
    while (window.__apolloClientCreating && attempts < maxAttempts && (Date.now() - startTime) < 100) {
      attempts++
    }
    if (window.__apolloClientInstance) {
      console.log('[Apollo] ✓ Got existing client after wait', { attempts, waitTime: Date.now() - startTime })
      return window.__apolloClientInstance
    }
  }
  
  // Set flag to indicate we're creating the client
  window.__apolloClientCreating = true
  const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown'
  console.log('[Apollo] 🔨 Starting Apollo Client creation...', {
    page: window.location.pathname,
    caller
  })

  // HTTP connection to the GraphQL API
  const httpLink = createHttpLink({
    uri: '/api',
  })

  // Error link to catch and handle all Apollo errors
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Handle GraphQL errors
    if (graphQLErrors && graphQLErrors.length > 0) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        console.error('[Apollo] GraphQL error:', {
          message,
          locations,
          path,
          operation: operation.operationName,
          extensions
        })
      })
    }

    // Handle network errors
    if (networkError) {
      const statusCode = networkError.statusCode || 
                        networkError.status || 
                        (networkError.response?.status) ||
                        (networkError.result?.status) ||
                        (networkError.networkError?.statusCode) ||
                        (networkError.networkError?.status)
      
      const errorMsg = networkError.message || networkError.toString() || 'Network error occurred'
      
      console.error('[Apollo] Network error:', {
        message: errorMsg,
        statusCode,
        operation: operation.operationName,
        networkError: networkError
      })
    }
  })

  // Restore cache from sessionStorage if available
  const restoredCacheData = restoreCache()
  
  // Cache implementation
  const cache = new InMemoryCache({
    typePolicies: {
      // Generic Entry type policy for proper normalization
      Entry: {
        keyFields: ['id'], // Apollo automatically includes __typename
        fields: {
          // Default merge for arrays - replace completely
          authors: {
            merge(existing, incoming) {
              return incoming
            }
          }
        }
      },
      // Query type policy for entries field (prevents cache data loss warning)
      Query: {
        fields: {
          entries: {
            // Cache queries with different variables separately
            // This ensures cache-and-network works properly for queries with different parameters
            keyArgs: ['section', 'siteId', 'authorId', 'uid'],
            // Replace entries array completely (server sends complete list)
            merge(existing, incoming) {
              return incoming
            }
          }
        }
      }
    }
  })
  
  // Restore cache data if available
  if (restoredCacheData) {
    try {
      cache.restore(restoredCacheData)
      console.log('[Apollo] ✓ Cache restored successfully')
    } catch (error) {
      console.warn('[Apollo] Failed to restore cache data:', error)
    }
  }

  // Create the Apollo Client (singleton, stored in window to persist across page loads)
  // Chain links: errorLink -> httpLink
  window.__apolloClientInstance = new ApolloClient({
    link: errorLink.concat(httpLink),
    cache,
    defaultOptions: {
      watchQuery: {
        // Default fetch policy - shows cached data immediately, fetches fresh in background
        fetchPolicy: 'cache-and-network',
        // After first fetch, use cache-first for subsequent loads (but network updates will still trigger)
        nextFetchPolicy: 'cache-first',
      },
      query: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  })

  // Generate a unique ID for this client instance
  const clientId = `apollo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  window.__apolloClientInstance._clientId = clientId
  
  // Persist cache periodically and on page unload
  const persistInterval = setInterval(() => {
    persistCache(cache)
  }, 5000) // Persist every 5 seconds
  
  // Store interval ID so we can clear it if needed
  window.__apolloCachePersistInterval = persistInterval
  
  // Persist cache on page unload
  window.addEventListener('beforeunload', () => {
    persistCache(cache)
    if (window.__apolloCachePersistInterval) {
      clearInterval(window.__apolloCachePersistInterval)
    }
  })
  
  // Clear the creating flag
  window.__apolloClientCreating = false
  
  console.log(`[Apollo] ✨ Created new client (ID: ${clientId})`, { 
    clientId,
    fetchPolicy: 'cache-and-network',
    page: window.location.pathname,
    hasRestoredCache: !!restoredCacheData
  })

  return window.__apolloClientInstance
}

// Provide Apollo Client to Vue app
export function provideApollo(app, apolloClient) {
  app.provide(DefaultApolloClient, apolloClient)
}

// Helper to read from Apollo cache synchronously
// Used by executeQueryWithCacheAndNetwork and can be used directly by components
export const readQueryFromCache = (apolloClient, query, variables = {}) => {
  try {
    const cachedData = apolloClient.readQuery({
      query,
      variables
    })
    return cachedData
  } catch (e) {
    // Cache miss - return null
    return null
  }
}

// Helper to execute query with cache-and-network
// Returns cached data immediately if available, then fetches fresh data in background
// If no cache, uses watchQuery with cache-and-network policy
// This is the centralized implementation used across all apps
// onUpdate: optional callback function called when fresh data arrives (for reactive UI updates)
export const executeQueryWithCacheAndNetwork = async (apolloClient, query, variables = {}, forceNetwork = false, onUpdate = null) => {
  const queryName = query.definitions?.[0]?.name?.value || 'Unknown'
  
  // First, try to read from cache synchronously
  if (!forceNetwork) {
    const cachedData = readQueryFromCache(apolloClient, query, variables)
    if (cachedData) {
      // Return cached data immediately, then fetch fresh data in background
      const entryCount = cachedData?.entries?.length || cachedData?.users?.length || 0
      console.log(`[Apollo] 🚀 Serving cached data immediately for ${queryName}`, {
        entryCount,
        note: 'Will fetch fresh data in background'
      })
      
      // Fetch fresh data in background (don't await)
      // Use watchQuery for background refresh so it can emit updates that trigger onUpdate callback
      const backgroundObservable = apolloClient.watchQuery({
        query,
        variables,
        fetchPolicy: 'network-only'
      })
      
      // Subscribe to background updates (but don't block)
      backgroundObservable.subscribe({
        next: (result) => {
          if (!result.loading && result.data) {
            const freshEntryCount = result.data?.entries?.length || result.data?.users?.length || 0
            console.log(`[Apollo] 🔄 Background refresh completed for ${queryName}`, {
              entryCount: freshEntryCount,
              note: 'Cache updated automatically by Apollo'
            })
            // Call onUpdate callback if provided (for reactive UI updates)
            if (onUpdate && typeof onUpdate === 'function') {
              onUpdate(result)
            }
          }
        },
        error: (err) => {
          console.warn(`[Apollo] ⚠️ Background refresh failed for ${queryName}`, {
            error: err.message,
            note: 'Using cached data'
          })
        }
      })
      
      return { data: cachedData, loading: false, networkStatus: 7 } // networkStatus 7 = ready
    }
  }
  
  // No cache, fetch from network with cache-and-network policy
  console.log(`[Apollo] ${forceNetwork ? '🔄 Force network fetch' : '❌ Cache MISS'} for ${queryName} - fetching from network`)
  const observable = apolloClient.watchQuery({
    query,
    variables,
    fetchPolicy: forceNetwork ? 'network-only' : 'cache-and-network'
  })
  
  // Subscribe and resolve
  return new Promise((resolve, reject) => {
    let hasResolved = false
    const subscription = observable.subscribe({
      next: (result) => {
        // When forceNetwork is true, wait for loading to complete to ensure we get fresh data
        // When forceNetwork is false, resolve immediately with cached data if available
        if (forceNetwork) {
          // Wait for network fetch to complete
          if (!result.loading && result.data && !hasResolved) {
            hasResolved = true
            subscription.unsubscribe()
            console.log(`[Apollo] ✅ Network fetch completed for ${queryName}`, {
              entryCount: result.data?.entries?.length || result.data?.users?.length || 0
            })
            resolve(result)
          }
        } else {
          // Resolve immediately with cached data if available
          if (result.data && !hasResolved) {
            hasResolved = true
            resolve(result)
          }
          // If no data yet but loading is complete, resolve anyway (error case or empty result)
          if (!result.loading && !hasResolved) {
            hasResolved = true
            subscription.unsubscribe()
            resolve(result)
          }
        }
      },
      error: (err) => {
        console.error(`[Apollo] ❌ Network fetch failed for ${queryName}`, err)
        subscription.unsubscribe()
        reject(err)
      }
    })
  })
}

